// mvp-returns Phase H10 — Benchmark widget for the trust score detail page.
// Shows tenant-wide averages and this buyer's percentile rank using only
// Mantine Progress bars and Text — no chart library.

import React from "react";
import {
  Box,
  Group,
  Progress,
  Skeleton,
  Stack,
  Text,
} from "@mantine/core";
import { Trans, useTranslation } from "react-i18next";
import useSWR from "swr";
import { TrustScoreApi } from "../../apis";
import type { TrustScoreLevel } from "../../apis/trust-score";
import { ListState } from "../ui";
import { DetailCard } from "./detail-card";
import { TRUST_LEVEL_COLOR, trustLevelLabel } from "./trust-level-badge";

interface IProps {
  businessId: string;
}

const LEVEL_ORDER: TrustScoreLevel[] = [
  "EXCELLENT",
  "GOOD",
  "MODERATE",
  "AT_RISK",
  "HIGH_RISK",
];

// ── small helpers ────────────────────────────────────────────────────────────

interface StatRowProps {
  label    : string;
  thisValue: number;
  avgValue : number;
  max      : number;
  color    : string;
  unit?    : string;
}

function StatRow({ label, thisValue, avgValue, max, color, unit = "" }: StatRowProps) {
  const { t } = useTranslation("returns");
  const thisPct = Math.min((thisValue / max) * 100, 100);
  const avgPct  = Math.min((avgValue  / max) * 100, 100);

  return (
    <Stack gap={4}>
      <Group justify="space-between" gap="xs" wrap="nowrap">
        <Text size="sm" c="var(--nw-text-2)" style={{ flexShrink: 0 }}>
          {label}
        </Text>
        <Group gap={4} wrap="nowrap">
          <Text size="sm" fw={600}>
            {thisValue.toFixed(1)}{unit}
          </Text>
          <Text size="sm" c="var(--nw-text-2)">
            {t("benchmark.avgSuffix", { value: `${avgValue.toFixed(1)}${unit}` })}
          </Text>
        </Group>
      </Group>
      {/* This business bar */}
      <Progress value={thisPct} color={color} size="sm" radius="xl" />
      {/* Tenant average bar */}
      <Progress value={avgPct}  color="gray"  size={4}  radius="xl" />
    </Stack>
  );
}

// ── main widget ──────────────────────────────────────────────────────────────

export function TrustBenchmarkWidget({ businessId }: IProps): React.JSX.Element {
  const { t } = useTranslation("returns");
  const { data, error, isLoading, mutate } = useSWR(
    ["trust-score.benchmark", businessId],
    () => TrustScoreApi.benchmark(businessId),
    { revalidateOnFocus: false }
  );

  if (isLoading) {
    return (
      <DetailCard>
        <Skeleton h={200} />
      </DetailCard>
    );
  }

  if (error) {
    return (
      <DetailCard>
        <ListState state="error" title={t("benchmark.loadError")} onRetry={() => mutate()} />
      </DetailCard>
    );
  }

  if (!data || data.tenant.total === 0) {
    return (
      <DetailCard>
        <Text size="sm" c="var(--nw-text-2)" ta="center">
          {t("benchmark.noData")}
        </Text>
      </DetailCard>
    );
  }

  const { tenant, business } = data;

  return (
    <DetailCard title={t("benchmark.title")}>

      {/* ── top: this business vs tenant average ─────────────────────────── */}
      <Group justify="space-between" gap="xs" mb="md" wrap="wrap">
        <Stack gap={2} align="center">
          <Text size="sm" c="var(--nw-text-2)">
            {t("benchmark.thisBuyer")}
          </Text>
          <Text size="xl" fw={700} c="brand">
            {business ? business.rawScore.toFixed(1) : "-"}
          </Text>
          <Text size="sm" c="var(--nw-text-2)">
            / 100
          </Text>
        </Stack>

        <Stack gap={2} align="center">
          <Text size="sm" c="var(--nw-text-2)">
            {t("benchmark.average")}
          </Text>
          <Text size="xl" fw={700}>
            {tenant.averageRawScore.toFixed(1)}
          </Text>
          <Text size="sm" c="var(--nw-text-2)">
            {t("benchmark.median", { value: tenant.medianRawScore.toFixed(1) })}
          </Text>
        </Stack>

        <Stack gap={2} align="center">
          <Text size="sm" c="var(--nw-text-2)">
            {t("benchmark.totalBuyers")}
          </Text>
          <Text size="xl" fw={700}>
            {tenant.total}
          </Text>
        </Stack>
      </Group>

      {/* ── percentile + rank ─────────────────────────────────────────────── */}
      {business && (
        <Stack gap={4} mb="md">
          <Text size="sm" fw={500}>
            <Trans
              i18nKey="benchmark.higherThan"
              ns="returns"
              values={{ pct: business.percentile.toFixed(1) }}
              components={[<span />, <Text span fw={700} c="brand" />]}
            />
          </Text>
          <Text size="sm" c="var(--nw-text-2)">
            {t("benchmark.rank", { rank: business.rank, total: business.total })}
          </Text>
        </Stack>
      )}

      {/* ── stat comparison bars ──────────────────────────────────────────── */}
      {business && (
        <Stack gap="sm" mb="md">
          <Text size="sm" fw={600} c="var(--nw-text-2)">
            {t("benchmark.statComparison")}
          </Text>
          <Text size="sm" c="var(--nw-text-2)">
            {t("benchmark.legend")}
          </Text>
          <StatRow
            label={t("benchmark.score")}
            thisValue={business.rawScore}
            avgValue={tenant.averageRawScore}
            max={100}
            color="brand"
          />
          <StatRow
            label={t("benchmark.returnRate")}
            thisValue={business.returnRatePercent}
            avgValue={tenant.averageReturnRate}
            max={Math.max(business.returnRatePercent, tenant.averageReturnRate, 1) * 1.5}
            color="gray.7"
            unit="%"
          />
          <StatRow
            label={t("benchmark.disputeRate")}
            thisValue={business.disputeRatePercent}
            avgValue={tenant.averageDisputeRate}
            max={Math.max(business.disputeRatePercent, tenant.averageDisputeRate, 1) * 1.5}
            color="gray.7"
            unit="%"
          />
        </Stack>
      )}

      {/* ── level distribution ────────────────────────────────────────────── */}
      <Stack gap={6}>
        <Text size="sm" fw={600} c="var(--nw-text-2)">
          {t("benchmark.levelDistribution")}
        </Text>
        {LEVEL_ORDER.map((level) => {
          const count = tenant.levelDistribution[level];
          const pct   = tenant.total > 0 ? (count / tenant.total) * 100 : 0;
          return (
            <Box key={level}>
              <Group justify="space-between" gap="xs" mb={2} wrap="nowrap">
                <Text size="sm" c="var(--nw-text-2)" style={{ flexShrink: 0 }}>
                  {trustLevelLabel(level)}
                  {/* O204 — the bound the tenant configured; the counts above use the same bounds. */}
                  {level !== "HIGH_RISK" && tenant.thresholds?.[level] != null
                    ? ` (${t("benchmark.thresholdFrom", { value: tenant.thresholds[level] })})`
                    : ""}
                </Text>
                <Text size="sm" fw={500}>
                  {count} ({pct.toFixed(0)}%)
                </Text>
              </Group>
              <Progress
                value={pct}
                color={TRUST_LEVEL_COLOR[level]}
                size="sm"
                radius="xl"
              />
            </Box>
          );
        })}
      </Stack>
    </DetailCard>
  );
}
